import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import apiClient from "@/lib/api-client";
import { unwrapApiArray, unwrapApiObject } from "@/lib/api-response";
import { useAuthStore } from "@/store/authStore";

export type TicketStatus = "open" | "in_progress" | "resolved";
export type TicketPriority = "low" | "medium" | "high";
export type TicketCategory =
  | "general"
  | "account"
  | "technical"
  | "placement"
  | "work_logs"
  | "payments";
export type FaqAudience = "all" | "student" | "partner";

export interface TicketMessage {
  id: number;
  ticket_id: number;
  author_id: number;
  is_staff: boolean;
  body: string;
  created_at: string;
  author_name?: string;
}

export interface SupportTicket {
  id: number;
  user_id: number;
  subject: string;
  category: TicketCategory;
  priority: TicketPriority;
  status: TicketStatus;
  description: string;
  last_reply_at?: string | null;
  resolved_at?: string | null;
  created_at: string;
  updated_at: string;
  user_name?: string;
  user_email?: string;
  user_role?: string;
  message_count: number;
  messages?: TicketMessage[];
}

export interface SupportFaq {
  id: number;
  question: string;
  answer: string;
  audience: FaqAudience;
  sort_order: number;
  published: boolean;
}

export interface SupportStats {
  open: number;
  in_progress: number;
  resolved_today: number;
  total: number;
}

export const TICKET_CATEGORIES: { value: TicketCategory; label: string }[] = [
  { value: "general", label: "General question" },
  { value: "account", label: "Account and sign-in" },
  { value: "technical", label: "Something isn't working" },
  { value: "placement", label: "Placements" },
  { value: "work_logs", label: "Work logs" },
  { value: "payments", label: "Payments and tips" },
];

export const categoryLabel = (c: string) =>
  TICKET_CATEGORIES.find((x) => x.value === c)?.label ?? c;

export const TICKET_STATUS: Record<
  TicketStatus,
  { label: string; variant: "destructive" | "warning" | "success" }
> = {
  open: { label: "Open", variant: "destructive" },
  in_progress: { label: "In progress", variant: "warning" },
  resolved: { label: "Resolved", variant: "success" },
};

export const TICKET_PRIORITY: Record<
  TicketPriority,
  { label: string; variant: "destructive" | "warning" | "muted" }
> = {
  high: { label: "High", variant: "destructive" },
  medium: { label: "Medium", variant: "warning" },
  low: { label: "Low", variant: "muted" },
};

// Demo accounts have no backend session, so they get a fixed sample.
const DEMO_TICKETS: SupportTicket[] = [
  {
    id: 1,
    user_id: 1,
    subject: "Unable to submit work log entries",
    category: "work_logs",
    priority: "high",
    status: "open",
    description: "The submit button does nothing on the work log page.",
    created_at: "2026-04-16T09:00:00Z",
    updated_at: "2026-04-16T09:00:00Z",
    user_name: "John Mwangi",
    user_role: "student",
    message_count: 0,
    messages: [],
  },
  {
    id: 2,
    user_id: 2,
    subject: "Query regarding student approval workflow",
    category: "placement",
    priority: "medium",
    status: "in_progress",
    description: "How long does a placement approval usually take?",
    created_at: "2026-04-15T09:00:00Z",
    updated_at: "2026-04-15T12:00:00Z",
    user_name: "CRDB Bank",
    user_role: "partner",
    message_count: 1,
    messages: [
      {
        id: 1,
        ticket_id: 2,
        author_id: 99,
        is_staff: true,
        body: "We are checking with the review team and will update you shortly.",
        created_at: "2026-04-15T12:00:00Z",
        author_name: "Taskpart support",
      },
    ],
  },
];

const DEMO_FAQS: SupportFaq[] = [
  {
    id: 1,
    question: "How do I register as a student?",
    answer:
      "Open the Taskpart home page, choose Register and pick the Student profile. You will be asked for your university and programme details during onboarding.",
    audience: "all",
    sort_order: 1,
    published: true,
  },
  {
    id: 2,
    question: "How are placements assigned?",
    answer:
      "Partners publish placement slots. Students apply to open slots, and the partner or a platform administrator reviews and approves each placement.",
    audience: "all",
    sort_order: 2,
    published: true,
  },
];

const demoBlocked = () => {
  toast.info("Not available in demo mode", {
    description: "Sign in with a real account to contact support.",
  });
  throw new Error("demo");
};

// ─── Students and partners ───

export function useSupportFaqs() {
  const isDemo = useAuthStore((s) => s.user?.isDemo);
  return useQuery({
    queryKey: ["support", "faqs"],
    queryFn: async () => {
      if (isDemo) return DEMO_FAQS;
      return unwrapApiArray<SupportFaq>(await apiClient.get("/support/faqs"));
    },
  });
}

export function useMyTickets() {
  const isDemo = useAuthStore((s) => s.user?.isDemo);
  return useQuery({
    queryKey: ["support", "my-tickets"],
    queryFn: async () => {
      if (isDemo) return DEMO_TICKETS.slice(0, 1);
      return unwrapApiArray<SupportTicket>(await apiClient.get("/support/tickets"));
    },
  });
}

export function useMyTicket(id: number | null) {
  const isDemo = useAuthStore((s) => s.user?.isDemo);
  return useQuery({
    queryKey: ["support", "my-ticket", id],
    enabled: id != null,
    queryFn: async () => {
      if (isDemo) return DEMO_TICKETS.find((t) => t.id === id) ?? null;
      return unwrapApiObject(
        await apiClient.get(`/support/tickets/${id}`),
      ) as unknown as SupportTicket;
    },
  });
}

export interface NewTicket {
  subject: string;
  category: TicketCategory;
  priority: TicketPriority;
  description: string;
}

export function useCreateTicket() {
  const queryClient = useQueryClient();
  const isDemo = useAuthStore((s) => s.user?.isDemo);
  return useMutation({
    mutationFn: async (input: NewTicket) => {
      if (isDemo) demoBlocked();
      return unwrapApiObject(
        await apiClient.post("/support/tickets", input),
      ) as unknown as SupportTicket;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["support"] }),
  });
}

export function useReplyToMyTicket(id: number | null) {
  const queryClient = useQueryClient();
  const isDemo = useAuthStore((s) => s.user?.isDemo);
  return useMutation({
    mutationFn: async (body: string) => {
      if (isDemo) demoBlocked();
      return unwrapApiObject(
        await apiClient.post(`/support/tickets/${id}/messages`, { body }),
      ) as unknown as SupportTicket;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["support"] }),
  });
}

// ─── Admin ───

export function useSupportStats() {
  const isDemo = useAuthStore((s) => s.user?.isDemo);
  return useQuery({
    queryKey: ["support", "admin", "stats"],
    queryFn: async () => {
      if (isDemo) return { open: 1, in_progress: 1, resolved_today: 0, total: 2 };
      return unwrapApiObject(
        await apiClient.get("/admin/support/stats"),
      ) as unknown as SupportStats;
    },
  });
}

export function useAdminTickets(status: string, search: string) {
  const isDemo = useAuthStore((s) => s.user?.isDemo);
  return useQuery({
    queryKey: ["support", "admin", "tickets", status, search],
    placeholderData: (prev) => prev,
    queryFn: async () => {
      if (isDemo) {
        const q = search.toLowerCase();
        return DEMO_TICKETS.filter(
          (t) =>
            (status === "all" || t.status === status) &&
            (t.subject.toLowerCase().includes(q) || (t.user_name ?? "").toLowerCase().includes(q)),
        );
      }
      const params: Record<string, string> = {};
      if (status !== "all") params.status = status;
      if (search.trim()) params.search = search.trim();
      return unwrapApiArray<SupportTicket>(
        await apiClient.get("/admin/support/tickets", { params }),
      );
    },
  });
}

export function useAdminTicket(id: number | null) {
  const isDemo = useAuthStore((s) => s.user?.isDemo);
  return useQuery({
    queryKey: ["support", "admin", "ticket", id],
    enabled: id != null,
    queryFn: async () => {
      if (isDemo) return DEMO_TICKETS.find((t) => t.id === id) ?? null;
      return unwrapApiObject(
        await apiClient.get(`/admin/support/tickets/${id}`),
      ) as unknown as SupportTicket;
    },
  });
}

export function useUpdateTicket(id: number | null) {
  const queryClient = useQueryClient();
  const isDemo = useAuthStore((s) => s.user?.isDemo);
  return useMutation({
    mutationFn: async (input: { status?: TicketStatus; priority?: TicketPriority }) => {
      if (isDemo) demoBlocked();
      return unwrapApiObject(
        await apiClient.patch(`/admin/support/tickets/${id}`, input),
      ) as unknown as SupportTicket;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["support"] }),
  });
}

export function useStaffReply(id: number | null) {
  const queryClient = useQueryClient();
  const isDemo = useAuthStore((s) => s.user?.isDemo);
  return useMutation({
    mutationFn: async (body: string) => {
      if (isDemo) demoBlocked();
      return unwrapApiObject(
        await apiClient.post(`/admin/support/tickets/${id}/messages`, { body }),
      ) as unknown as SupportTicket;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["support"] }),
  });
}

export function useAdminFaqs() {
  const isDemo = useAuthStore((s) => s.user?.isDemo);
  return useQuery({
    queryKey: ["support", "admin", "faqs"],
    queryFn: async () => {
      if (isDemo) return DEMO_FAQS;
      return unwrapApiArray<SupportFaq>(await apiClient.get("/admin/support/faqs"));
    },
  });
}

export type FaqInput = Omit<SupportFaq, "id">;

export function useSaveFaq() {
  const queryClient = useQueryClient();
  const isDemo = useAuthStore((s) => s.user?.isDemo);
  return useMutation({
    mutationFn: async ({ id, ...input }: FaqInput & { id?: number }) => {
      if (isDemo) demoBlocked();
      const res = id
        ? await apiClient.put(`/admin/support/faqs/${id}`, input)
        : await apiClient.post("/admin/support/faqs", input);
      return unwrapApiObject(res) as unknown as SupportFaq;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["support"] }),
  });
}

export function useDeleteFaq() {
  const queryClient = useQueryClient();
  const isDemo = useAuthStore((s) => s.user?.isDemo);
  return useMutation({
    mutationFn: async (id: number) => {
      if (isDemo) demoBlocked();
      await apiClient.delete(`/admin/support/faqs/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["support"] }),
  });
}
